import { AccountAddress, AptosApiError } from "@aptos-labs/ts-sdk";

import { BaseReader } from "../base-reader";
import {
  PendingWithdrawRequest,
  PendingWithdrawRequestSchema,
  WithdrawQueueRequestArgs,
  WithdrawQueueResponse,
  WithdrawQueueResponseSchema,
  WithdrawQueueUpdate,
  WithdrawQueueUpdateSchema,
} from "./withdraw-queue.types";

/**
 * Reader for withdrawal queue data.
 *
 * Provides three data paths:
 * - `getByAddr` / `subscribeByAddr` — indexed HTTP + WS API (primary data source)
 * - `getPendingWithdrawals` — direct on-chain RPC view call (liveness-check fallback only,
 *   bypasses HTTP auth headers / API key injection; returns raw chain units)
 */
export class WithdrawQueueReader extends BaseReader {
  /**
   * Get withdrawal queue entries for an account from the indexed API.
   *
   * Use `request_id` as the stable key to reconcile entries with WS updates.
   *
   * **Deduplication:** Without a `status` filter the response may contain multiple
   * rows per `request_id` (e.g. the original Queued row and the terminal
   * Processed/Cancelled row). Consumers should deduplicate by `request_id`,
   * keeping the entry with the highest `transaction_version`.
   *
   * Offset-based pagination is best suited for historical snapshots. For real-time
   * tracking, prefer a status filter (e.g. `status: "Queued"`) combined with
   * `subscribeByAddr` for live updates.
   *
   * The server defaults to `limit=10, offset=0` when not specified (max `limit` is 200).
   *
   * **`total_count` overcounting:** Without a `status` filter, each state transition is a
   * separate row — a Queued-then-Processed withdrawal contributes 2 rows to `total_count`.
   * Always pass a `status` filter when using `total_count` for pagination display
   * (e.g. "showing X of Y"), or deduplicate client-side by `request_id` before counting.
   */
  async getByAddr({
    subAddr,
    status,
    limit,
    offset,
    fetchOptions,
  }: WithdrawQueueRequestArgs): Promise<WithdrawQueueResponse> {
    const queryParams: Record<string, string> = {
      account: subAddr,
    };
    if (limit !== undefined) {
      queryParams.limit = limit.toString();
    }
    if (offset !== undefined) {
      queryParams.offset = offset.toString();
    }
    if (status !== undefined) {
      queryParams.status = status;
    }

    const response = await this.getRequest({
      schema: WithdrawQueueResponseSchema,
      url: `${this.deps.config.tradingHttpUrl}/api/v1/withdraw_queue`,
      queryParams,
      options: fetchOptions,
    });

    return response.data;
  }

  /**
   * Subscribe to real-time withdrawal queue updates for a user address.
   *
   * **No initial snapshot — subscribe first, then seed.** This topic is streaming-only.
   * The correct initialization order is:
   *   1. Call `subscribeByAddr` to start receiving deltas
   *   2. Call `getByAddr` to fetch the current snapshot
   *   3. Merge: `mergeWithdrawQueueEntries({ existing: wsCache, delta: httpSnapshot })`
   *
   * **Race window caveat:** `subscribeByAddr` registers the handler locally and sends
   * the WS SUBSCRIBE frame, but the server may not process it before a subsequent
   * `getByAddr` HTTP request completes. Events emitted in this sub-millisecond window
   * could be missed. The merge utility mitigates this: HTTP snapshot data cannot regress
   * WS-advanced entries (version guard), and any missed events will appear in the next
   * WS batch or HTTP re-fetch.
   *
   * Subscribing *before* fetching ensures no events are lost between the HTTP
   * response and subscription activation. On WS reconnect, repeat the same
   * sequence: re-subscribe first, then re-call `getByAddr` to re-seed.
   *
   * All messages are incremental deltas. Consumers must merge by `request_id`:
   * only apply a delta when its `transaction_version` is strictly greater than (`>`)
   * the cached entry's, and keep the entry with the highest `transaction_version`.
   * Using `>` (not `>=`) is important: the indexer uses at-least-once delivery, so
   * duplicate events with the same `transaction_version` can arrive — accepting `>=`
   * would re-overwrite field-level merged values with null from the duplicate.
   * Do not replace the full cache — WS batches contain only the entries from the
   * current indexer batch, not the user's complete history.
   *
   * **Field-level merge required:** `recipient`, `market`, and `queued_at_ms` may be null on
   * Processed/Cancelled entries when the original Queued event was not in the same
   * batch (the HTTP endpoint enriches these via a DB backfill; the WS does not).
   * When merging, preserve existing non-null values for these fields rather than
   * overwriting with null from a WS delta. Re-fetch from the HTTP endpoint when
   * enriched data is needed.
   *
   * @param subAddr - Hex address of the subaccount (e.g. `0x1a2b...`)
   */
  subscribeByAddr(subAddr: string, onData: (data: WithdrawQueueUpdate) => void) {
    const topic = `withdraw_queue:${subAddr}`;
    return this.deps.ws.subscribe(topic, WithdrawQueueUpdateSchema, onData);
  }

  /**
   * Get pending withdrawal requests for a user from the async withdrawal queue (on-chain view).
   *
   * **This is a liveness-check fallback**, not the primary data source. It only returns
   * currently-Queued items in raw chain units. For the full history (all statuses, normalized
   * amounts), use `getByAddr()` instead.
   *
   * To correlate with indexed data, use `request_id` as the shared key.
   *
   * Returns an empty array if the queue module is not initialized or the user has no pending requests.
   * **Note:** `resource_not_found` is also swallowed — this means a misconfigured `package` address
   * would silently return `[]` rather than throwing. This is an accepted tradeoff: this method is a
   * liveness-check fallback, not the primary data source, and the alternative (distinguishing
   * "user has no resource" from "module misconfigured") requires additional RPC calls.
   * Throws on unexpected errors (RPC failures, schema validation failures, etc.).
   * A `ZodError` indicates the on-chain struct shape has changed — monitor after contract upgrades.
   */
  async getPendingWithdrawals(
    userAddr: string | AccountAddress,
  ): Promise<PendingWithdrawRequest[]> {
    const normalized = AccountAddress.from(userAddr);
    try {
      const result = await this.deps.aptos.view<[unknown[]]>({
        payload: {
          function: `${this.deps.config.deployment.package}::async_withdraw_queue::get_pending_withdrawals`,
          typeArguments: [],
          functionArguments: [normalized.toString()],
        },
      });
      return result[0].map((item) => PendingWithdrawRequestSchema.parse(item));
    } catch (e) {
      if (isModuleNotFoundError(e)) {
        return [];
      }
      throw e;
    }
  }
}

function isModuleNotFoundError(e: unknown): boolean {
  if (e instanceof AptosApiError) {
    const data = e.data as Record<string, unknown> | undefined;
    if (data == null || typeof data !== "object") return false;
    const code = data.error_code;
    if (code === "module_not_found" || code === "function_not_found") {
      return true;
    }
    // resource_not_found is expected when the user has never interacted with the
    // withdraw queue (no on-chain resource exists). Note: a misconfigured
    // `deployment.package` address also produces this error and will silently
    // return [] — see the JSDoc on getPendingWithdrawals for the accepted tradeoff.
    if (code === "resource_not_found") {
      return true;
    }
  }
  return false;
}
